import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable';
import AutoResizeTextarea from '@eeacms/volto-chatbot/ChatBlock/AutoResizeTextarea';
import EmptyState from '@eeacms/volto-chatbot/ChatBlock/EmptyState';
import { useBackendChat } from '@eeacms/volto-chatbot/ChatBlock/useBackendChat';
import '@eeacms/volto-chatbot/ChatBlock/style.less';

import { ChatMessageBubble } from './ChatMessageBubble';
import { canSendChatbotMessage, CHATBOT_CONTACT_EMAIL, CHATBOT_OPEN_EVENT, DEFAULT_CHATBOT_ASSISTANT_ID, DEFAULT_QUALITY_CHECK_STAGES, getChatbotContactPath, getChatbotModerationMessage, getChatbotRateLimitMessage, isIskraRelated } from './helpers';
import { chatbotFeedbackReasonMessages, chatbotMessages, chatbotPromptMessages } from './messages';

const BLANK_SLATE_VALUE = [
  {
    type: 'p',
    children: [{ text: '' }],
  },
];

const IskraChatbotWidgetComponent = ({ rehypePrism, remarkGfm }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = React.useState(false);
  const [assistantData, setAssistantData] = React.useState(null);
  const [assistantError, setAssistantError] = React.useState('');
  const [queuedQuestion, setQueuedQuestion] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const messagesRef = React.useRef(null);

  const assistantId = DEFAULT_CHATBOT_ASSISTANT_ID;
  const contactPath = React.useMemo(() => getChatbotContactPath(intl.locale), [intl.locale]);
  const starterPrompts = React.useMemo(
    () =>
      chatbotPromptMessages.map((descriptor) => {
        const text = intl.formatMessage(descriptor);

        return {
          name: text,
          message: text,
        };
      }),
    [intl],
  );
  const feedbackReasons = React.useMemo(() => chatbotFeedbackReasonMessages.map((descriptor) => intl.formatMessage(descriptor)), [intl]);

  const eyebrowLabel = intl.formatMessage(chatbotMessages.chatbotEyebrow);
  const closeLabel = intl.formatMessage(chatbotMessages.close);
  const contactUsLabel = intl.formatMessage(chatbotMessages.contactUs);
  const defaultChatbotTitle = intl.formatMessage(chatbotMessages.defaultChatbotTitle);
  const defaultChatbotPlaceholder = intl.formatMessage(chatbotMessages.defaultChatbotPlaceholder);
  const defaultChatbotFollowUpPlaceholder = intl.formatMessage(chatbotMessages.defaultChatbotFollowUpPlaceholder);
  const defaultChatbotPromptsHeading = intl.formatMessage(chatbotMessages.defaultChatbotPromptsHeading);
  const defaultHeaderDescription = intl.formatMessage(chatbotMessages.defaultHeaderDescription, {
    email: CHATBOT_CONTACT_EMAIL,
  });
  const introMessage = intl.formatMessage(chatbotMessages.introMessage);
  const newConversationLabel = intl.formatMessage(chatbotMessages.newConversation);
  const loadingAssistantMessage = intl.formatMessage(chatbotMessages.loadingAssistant);
  const loadAssistantErrorMessage = intl.formatMessage(chatbotMessages.loadAssistantError);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
    setQueuedQuestion('');
    setErrorMessage('');
  }, []);

  const { onSubmit, messages, isStreaming, clearChat, isFetchingRelatedQuestions } = useBackendChat({
    persona: assistantData || { id: assistantId },
    qgenAsistantId: undefined,
    enableQgen: false,
  });

  React.useEffect(() => {
    let isMounted = true;

    const loadAssistant = async () => {
      try {
        setAssistantError('');
        const response = await fetch(`/_v1_da/persona/${assistantId}`);
        const body = await response.json();

        if (!response.ok || body?.error) {
          throw new Error(body?.error || loadAssistantErrorMessage);
        }

        if (isMounted) {
          setAssistantData(body);
        }
      } catch (error) {
        if (isMounted) {
          setAssistantError(error?.message || loadAssistantErrorMessage);
        }
      }
    };

    loadAssistant();

    return () => {
      isMounted = false;
    };
  }, [assistantId, loadAssistantErrorMessage]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleOpen = (event) => {
      setErrorMessage('');
      setQueuedQuestion(event?.detail?.question || '');
      setIsOpen(true);
    };

    window.addEventListener(CHATBOT_OPEN_EVENT, handleOpen);

    return () => {
      window.removeEventListener(CHATBOT_OPEN_EVENT, handleOpen);
    };
  }, []);

  React.useEffect(() => {
    const node = messagesRef.current;

    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [messages, isStreaming, isFetchingRelatedQuestions]);

  React.useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose, isOpen]);

  const handleQuestionSubmit = React.useCallback(
    (question) => {
      const nextQuestion = question?.trim();

      if (!nextQuestion || !assistantData) {
        return false;
      }

      if (!canSendChatbotMessage()) {
        setErrorMessage(getChatbotRateLimitMessage(intl));
        return false;
      }

      if (!isIskraRelated(nextQuestion)) {
        setErrorMessage(getChatbotModerationMessage(intl));
        return false;
      }

      setErrorMessage('');
      onSubmit({ message: nextQuestion });
      setIsOpen(true);
      return true;
    },
    [assistantData, intl, onSubmit],
  );

  React.useEffect(() => {
    if (!queuedQuestion || !assistantData || isStreaming) {
      return;
    }

    handleQuestionSubmit(queuedQuestion);
    setQueuedQuestion('');
  }, [assistantData, handleQuestionSubmit, isStreaming, queuedQuestion]);

  const headerTitle = assistantData?.name || defaultChatbotTitle;
  const headerDescription = assistantData?.description || defaultHeaderDescription;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="iskra-chatbot-widget">
      <button type="button" className="iskra-chatbot-widget__overlay" onClick={handleClose} tabIndex={-1} aria-hidden="true" />
      <div className="iskra-chatbot-widget__panel" role="dialog" aria-modal="true" aria-labelledby="iskra-chatbot-widget-title">
        <div className="iskra-chatbot-widget__header">
          <div>
            <p className="iskra-chatbot-widget__eyebrow">{eyebrowLabel}</p>
            <h2 id="iskra-chatbot-widget-title" className="iskra-chatbot-widget__title">
              {headerTitle}
            </h2>
            <p className="iskra-chatbot-widget__description">{headerDescription}</p>
          </div>

          <div className="iskra-chatbot-widget__header-actions">
            {messages.length > 0 && (
              <button
                type="button"
                className="iskra-chatbot-widget__ghost-button"
                onClick={() => {
                  clearChat();
                  setErrorMessage('');
                }}
              >
                {newConversationLabel}
              </button>
            )}
            <button type="button" className="iskra-chatbot-widget__close-button" onClick={handleClose}>
              {closeLabel}
            </button>
          </div>
        </div>

        <div className="iskra-chatbot-widget__content">
          {assistantError ? (
            <div className="iskra-chatbot-widget__status is-error">{assistantError}</div>
          ) : !assistantData ? (
            <div className="iskra-chatbot-widget__status">{loadingAssistantMessage}</div>
          ) : (
            <div className="chat-window">
              <div className="messages">
                {messages.length === 0 ? (
                  <div className="iskra-chatbot-widget__empty-state">
                    <p className="iskra-chatbot-widget__intro">{introMessage}</p>
                    <EmptyState persona={assistantData} onChoice={handleQuestionSubmit} showAssistantPrompts={false} enableStarterPrompts={true} starterPrompts={starterPrompts} starterPromptsHeading={defaultChatbotPromptsHeading} />
                  </div>
                ) : (
                  <div ref={messagesRef} className="conversation include-scrollbar">
                    {messages.map((message, index) => (
                      <ChatMessageBubble key={`${message.messageId}-${index}`} message={message} isLoading={isStreaming} enableFeedback={true} feedbackReasons={feedbackReasons} libs={{ rehypePrism, remarkGfm }} qualityCheck="disabled" qualityCheckStages={DEFAULT_QUALITY_CHECK_STAGES} qualityCheckContext="citations" qualityCheckEnabled={false} onChoice={handleQuestionSubmit} noSupportDocumentsMessage={BLANK_SLATE_VALUE} enableShowTotalFailMessage={false} totalFailMessage={BLANK_SLATE_VALUE} showToolCalls={true} isFetchingRelatedQuestions={isFetchingRelatedQuestions} enableMatomoTracking={false} persona={assistantData} maxContextSegments={0} />
                    ))}
                  </div>
                )}

                {isStreaming && !isFetchingRelatedQuestions && <div className="loader" />}
              </div>

              {errorMessage && <div className="iskra-chatbot-widget__message is-error">{errorMessage}</div>}

              <div className="chat-form">
                <Form>
                  <div className="textarea-wrapper">
                    <AutoResizeTextarea placeholder={messages.length > 0 ? defaultChatbotFollowUpPlaceholder : defaultChatbotPlaceholder} isStreaming={isStreaming || !assistantData} enableMatomoTracking={false} persona={assistantData} onSubmit={({ message }) => handleQuestionSubmit(message)} />
                  </div>
                </Form>
              </div>
            </div>
          )}
        </div>

        <div className="iskra-chatbot-widget__footer">
          <a href={contactPath}>{contactUsLabel}</a>
          <a href={`mailto:${CHATBOT_CONTACT_EMAIL}`}>{CHATBOT_CONTACT_EMAIL}</a>
        </div>
      </div>
    </div>
  );
};

IskraChatbotWidgetComponent.propTypes = {
  rehypePrism: PropTypes.any,
  remarkGfm: PropTypes.any,
};

export const IskraChatbotWidget = injectLazyLibs(['rehypePrism', 'remarkGfm'])(IskraChatbotWidgetComponent);

export default IskraChatbotWidget;
